import { CalendarRange } from "lucide-react";
import { toast } from "sonner";

import type { B2cStageRow } from "@/infrastructure/supabase/b2c-board.types";

function formatDay(date: string | null): string | null {
  if (!date) return null;
  const [, m, d] = date.split("-");
  return m && d ? `${Number(d)}.${Number(m)}` : null;
}

/** The start/end pair a stage or a workstream saves (both rows share these columns). */
export type DateRangePatch = Partial<Pick<B2cStageRow, "start_date" | "end_date">>;

/**
 * A start → end date pair. Writers get two native date pickers that save on
 * change; an end before the start is refused. Viewers see "18.10 → 1.11".
 */
export function EditableDateRange({
  start,
  end,
  canWrite,
  onChange,
  label,
}: {
  start: string | null;
  end: string | null;
  canWrite: boolean;
  onChange: (patch: DateRangePatch) => void;
  /** Names the range for screen readers, e.g. the workstream's title. */
  label: string;
}) {
  if (!canWrite) {
    const from = formatDay(start);
    const to = formatDay(end);
    return (
      <span className="font-data text-muted-foreground text-[12px]">
        {from || to ? `${from ?? "?"} → ${to ?? "?"}` : "No dates"}
      </span>
    );
  }

  const set = (field: "start_date" | "end_date", value: string) => {
    const next = value || null;
    const nextStart = field === "start_date" ? next : start;
    const nextEnd = field === "end_date" ? next : end;
    if (nextStart && nextEnd && nextEnd < nextStart) {
      toast.error("The end date can’t be before the start date.");
      return;
    }
    onChange(field === "start_date" ? { start_date: next } : { end_date: next });
  };

  const inputClass =
    "font-data border-border hover:border-primary/40 focus-visible:border-primary h-7 rounded-md border bg-transparent px-1.5 text-[11.5px] tabular-nums focus-visible:outline-none";

  return (
    <span className="text-muted-foreground flex items-center gap-1.5">
      <CalendarRange className="size-3.5" aria-hidden />
      <input
        type="date"
        value={start ?? ""}
        max={end ?? undefined}
        onChange={(e) => set("start_date", e.target.value)}
        aria-label={`${label} start date`}
        className={inputClass}
      />
      <span aria-hidden>→</span>
      <input
        type="date"
        value={end ?? ""}
        min={start ?? undefined}
        onChange={(e) => set("end_date", e.target.value)}
        aria-label={`${label} end date`}
        className={inputClass}
      />
    </span>
  );
}
